/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { ASSIGNMENT, QUESTION, SURVEY } from './terminology';

export interface RoleInformation<T = UserRoles> {
  disabled?: boolean;
  code: T;
  label: string;
  icon: string;
  description: string;
  requiresVerificationAccess?: boolean;
}

export enum UserRoles {
  Owner = 'owner',
  Manager = 'manager',
  User = 'user',
  Contributor = 'contributor',
  Verifier = 'verifier',
  ContributorVerifier = 'contributor_verifier',
  Guest = 'guest',
  Viewer = 'viewer'
}

export const USER_ROLE_LABEL_MAP = {
  [UserRoles.Owner]: 'Owner',
  [UserRoles.Manager]: 'Administrator',
  [UserRoles.Contributor]: 'Contributor',
  [UserRoles.Verifier]: 'Verifier',
  [UserRoles.ContributorVerifier]: 'Contributor and Verifier',
  [UserRoles.User]: 'User',
  [UserRoles.Viewer]: 'Viewer',
  /* @deprecated */
  [UserRoles.Guest]: 'Guest',
};

export type UserFilterReportingLevel = 'inherited' | 'inherited_and_subsidiaries' | undefined;

export const companyTrackerUserRoles: RoleInformation[] = [
  {
    disabled: true,
    code: UserRoles.Owner,
    label: 'Owner',
    icon: 'fa-user',
    description: '',
  },
  {
    code: UserRoles.Manager,
    label: 'Administrator',
    icon: 'fa-user',
    description: `Administrators are able to modify all details of your organisation, including billing, materiality, custom metrics, manage users, and creating new ${SURVEY.PLURAL}. This permission gives full access to the platform and so it is recommended to limit the number of users with this permission.`,
  },
  {
    code: UserRoles.Contributor,
    label: 'Contributor',
    icon: 'fa-user',
    description: `Contributors are able to contribute answers to all ${QUESTION.PLURAL} for all ${SURVEY.PLURAL} in your organisation. Contributors can also verify ${QUESTION.PLURAL}, but only if they have been ${ASSIGNMENT.explicit} as a verifier to a ${QUESTION.SINGULAR} or ${SURVEY.SINGULAR}.`,
  },
  {
    code: UserRoles.Verifier,
    label: 'Verifier',
    icon: 'fa-user',
    description: `Verifiers are able to verify all ${QUESTION.PLURAL} that have been assigned as in-scope for ${SURVEY.PLURAL} in your organisation. Verifiers can also contribute to ${QUESTION.PLURAL}, but only if they have been ${ASSIGNMENT.explicit} as a contributor to a ${QUESTION.SINGULAR} or ${SURVEY.SINGULAR}.`,
    requiresVerificationAccess: true,
  },
  {
    code: UserRoles.ContributorVerifier,
    label: 'Contributor and Verifier',
    icon: 'fa-user',
    description: `Accounts with this permission are able to contribute answers to all ${QUESTION.PLURAL} and verify all ${QUESTION.PLURAL} that have been assigned as in-scope for ${SURVEY.PLURAL} in your organisation, without needing to be ${ASSIGNMENT.explicit} to the ${QUESTION.SINGULAR} by an admin.`,
    requiresVerificationAccess: true,
  },
  {
    code: UserRoles.Viewer,
    label: 'Viewer',
    icon: 'fa-user',
    description: `Viewers are able to view all ${QUESTION.PLURAL}, but can only contribute or verify if they are ${ASSIGNMENT.explicit} to a ${QUESTION.SINGULAR} or ${SURVEY.SINGULAR}.`,
  },
  {
    code: UserRoles.User,
    label: 'Restricted user',
    icon: 'fa-user',
    description: `This is the default role. Users are unable to view, contribute or verify ${QUESTION.PLURAL} unless they are ${ASSIGNMENT.explicit} to a ${QUESTION.SINGULAR} or ${SURVEY.SINGULAR}.`,
  },
  {
    disabled: true,
    code: UserRoles.Guest,
    label: 'Guest',
    icon: 'fa-user',
    description:
      'This is the most basic access user. They will have limited access to the company profile but are unable to see private data or navigate reporting levels. This is the recommended role for users outside your organization.',
  },
];

export const portfolioTrackerUserRoles: RoleInformation[] = [
  {
    code: UserRoles.Manager,
    label: 'Administrator',
    icon: 'fa-user',
    description:
      'This user is able to manage this account, including managing custom metrics, users, and portfolios. This role gives full access and is recommended only for administrators.',
  },
  {
    code: UserRoles.Viewer,
    label: 'Viewer',
    icon: 'fa-user',
    description: 'Viewers are able to view and download data.',
  },
];

export enum OrganizationUserRole {
  Admin = 'admin',
  Assurer = 'user',
  Viewer = 'viewer',
  RestrictedUser = 'restricted',
}

export const assuranceTrackerUserRoles: RoleInformation<OrganizationUserRole>[] = [
  {
    code: OrganizationUserRole.Admin,
    label: 'Administrator',
    icon: 'fa-user',
    description:
      'Administrators are able to access, view and act as the assurance manager for all assurance portfolios.',
  },
  {
    code: OrganizationUserRole.Assurer,
    label: 'Assurer',
    icon: 'fa-user',
    description:
      'Assurers are able to access to all assurance portfolios and are able to assure individual metrics within each assurance portfolio.',
  },
  {
    code: OrganizationUserRole.Viewer,
    label: 'Viewer',
    icon: 'fa-user',
    description:
      'Viewers are able to access to all assurance portfolios, but are not able to assure unless explicitly assigned to the assurance portfolio.',
  },
  {
    code: OrganizationUserRole.RestrictedUser,
    label: 'Restricted user',
    icon: 'fa-user',
    description:
      'Restricted users are able to access only assurance portfolios where they have been explicitly invited.',
  },
];

export const filterValidRoles = (role: UserRoles) => Object.values(UserRoles).includes(role);


export const DISABLED_DELEGATION_MESSAGE = `This user needs to be invited to the current ${SURVEY.ADJECTIVE} level first`;

export enum SurveyUserRoles {
  Admin = 'admin',
  Verifier = 'verifier',
  Stakeholder = 'stakeholder',
  Monitor = 'viewer'
}

export type UtrvUserRole = SurveyUserRoles.Stakeholder | SurveyUserRoles.Verifier;

/**
 * Not including survey level delegation, but might need to be added in the future
 */
export enum DelegationRoles {
  UtrvStakeholder = 'utrvStakeholder',
  UtrvVerifier = 'utrvVerifier',
  InitiativeStakeholder = 'initiativeStakeholder',
  InitiativeVerifier = 'initiativeVerifier',
}
